import { useEffect, useRef, useState } from 'react';

/* Keep in sync with the drawer exit animation duration in modal.css. */
export const DRAWER_EXIT_MS = 200;

/**
 * Keeps a drawer mounted for its exit animation after `show` turns false,
 * rendering the last children it had while open (callers often clear the
 * data behind the content at the same moment they close the drawer).
 *
 * @param {boolean} show
 * @param {React.ReactNode} children
 * @param {{ enabled?: boolean }} options - `enabled: false` unmounts immediately
 * @returns {{ isMounted: boolean, isClosing: boolean, content: React.ReactNode }}
 */
const useExitTransition = (show, children, { enabled = true } = {}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [wasShown, setWasShown] = useState(show);
  const lastChildrenRef = useRef(children);

  if (show) {
    lastChildrenRef.current = children;
  }

  if (show && !wasShown) {
    setWasShown(true);
    setIsClosing(false);
  } else if (!show && wasShown) {
    setWasShown(false);
    if (enabled) setIsClosing(true);
  }

  useEffect(() => {
    if (!isClosing) return undefined;
    const timer = setTimeout(() => setIsClosing(false), DRAWER_EXIT_MS);
    return () => clearTimeout(timer);
  }, [isClosing]);

  return {
    isMounted: show || isClosing,
    isClosing,
    content: show ? children : lastChildrenRef.current,
  };
};

export default useExitTransition;
